import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
    Accordion,
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
    Avatar,
    Badge,
    Button,
    DataGrid,
    DataGridBody,
    DataGridCell,
    DataGridHeader,
    DataGridHeaderCell,
    DataGridProps,
    DataGridRow,
    Label,
    Spinner,
    TableCellLayout,
    TableColumnDefinition,
    TableRowId,
    Text,
    createTableColumn,
} from '@fluentui/react-components';
import {
    AlertOn16Regular,
    BranchFork16Filled,
    CoinStack16Regular,
    DeleteRegular,
    EditRegular,
    Important16Regular,
    Bot20Regular,
    Person16Regular,
    Person24Regular,
    QuestionCircle16Regular,
} from '@fluentui/react-icons';
import moment from 'moment';
import { FontSizes } from '@fluentui/react';
import { useTranslation } from 'react-i18next';

import * as ComponentFunctions from './ProtocolConsumptionListByPatient.Functions';
import { GetConsumptionOrigin, GetRowClassByStatus } from '../ConsumptionList/ConsumptionList.Functions';
import { ProtocolConsumption } from '../../../models/Entities/Protocols/ProtocolConsumption';

// Mismo componente que ConsumptionList: esta grilla muestra los mismos consumos
// y se rompía igual cuando el consumo venía sin moneda.
import CurrencyAmountComponent from '../../../commons/component/CurrencyAmount/CurrencyAmount.Component';
import { ProtocolConsumptionConditionEnum } from '../../../models/Enums/ProtocolConsumptionConditionEnum';
import { ProtocolPatient } from '../../../models/Entities/Protocols/ProtocolPatient';
import ConsumptionDetailsCompoment from '../ConsumptionDetails/ConsumptionDetails.Component';

import '../ConsumptionList/ConsumptionList.Styles.css';

// ─── Inner grid ─────────────────────────────────────────────────────────────

interface PatientGridProps {
    consumptions: ProtocolConsumption[];
    showGroupedByVisit?: boolean;
    GetPersonByIdentifier: (identifier: string) => any;
    GetProtocolById?: (protocolId: number) => any;
    onOpenDetails: (consumption: ProtocolConsumption) => void;
    onDeleteConsumption?: (consumption: ProtocolConsumption) => void;
    onRowSelectionChange?: (selected: ProtocolConsumption[]) => void;
    ProviderId?: number;
    StageId?: number;
    ProtocolId?: number;
}

const PatientConsumptionsGrid: React.FC<PatientGridProps> = ({
    consumptions,
    showGroupedByVisit,
    GetPersonByIdentifier,
    GetProtocolById,
    onOpenDetails,
    onDeleteConsumption,
    onRowSelectionChange,
    ProviderId,
    StageId,
    ProtocolId,
}) => {
    const { t } = useTranslation();
    const [selectedRows, setSelectedRows] = useState(new Set<TableRowId>());
    const onSelectionChange: DataGridProps['onSelectionChange'] = (e, data) => {
        setSelectedRows(data.selectedItems);
        if (onRowSelectionChange) {
            onRowSelectionChange(consumptions.filter(c => data.selectedItems.has(c.id)));
        }
    };

    const conceptColumnDefs = useMemo(() => {
        const seen = new Set<string>();
        consumptions.forEach((c) => {
            c.conceptValues?.forEach((cv) => seen.add(cv.contractConceptName));
        });
        return Array.from(seen).map((conceptName) =>
            createTableColumn<ProtocolConsumption>({
                columnId: `concept-value-${conceptName}`,
                compare: (a, b) => {
                    const aVal = (a.conceptValues ?? []).filter(cv => cv.contractConceptName === conceptName).reduce((sum, cv) => sum + cv.value, 0);
                    const bVal = (b.conceptValues ?? []).filter(cv => cv.contractConceptName === conceptName).reduce((sum, cv) => sum + cv.value, 0);
                    return aVal - bVal;
                },
                renderHeaderCell: () => <span>{conceptName}</span>,
                renderCell: (item: ProtocolConsumption) => {
                    const matches = (item.conceptValues ?? []).filter(cv => cv.contractConceptName === conceptName);
                    if (matches.length === 0) return <TableCellLayout>—</TableCellLayout>;
                    const total = matches.reduce((sum, cv) => sum + cv.value, 0);
                    return (
                        <TableCellLayout>
                            <strong><CurrencyAmountComponent amount={total} currency={matches[0].currencyCode} /></strong>
                        </TableCellLayout>
                    );
                },
            })
        );
    }, [consumptions]);

    const visibleConsumptions = useMemo(() => {
        if (showGroupedByVisit === false) {
            return consumptions;
        }
        return consumptions.filter((item) => !item.parentGroupingId);
    }, [consumptions, showGroupedByVisit]);

    const columns: (TableColumnDefinition<ProtocolConsumption> | false)[] = [
        createTableColumn<ProtocolConsumption>({
            columnId: 'status',
            compare: (a, b) => (ProviderId ? a.costStatus - b.costStatus : a.status - b.status),
            renderHeaderCell: () => <span>{t('consumptionList.headers.status')}</span>,
            renderCell: (item) => (
                <TableCellLayout>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {/* isDeleting entra igual que en la grilla general: sin él la
                            fila no muestra nada mientras se está borrando. */}
                        {item.ischangingStatus || item.isDeleting || item.isSaving
                            ? <Spinner size="extra-tiny" />
                            : <div
                                className={'status-mark ' + ComponentFunctions.GetStatusDescription(ProviderId ? item.costStatus : item.status).class}
                                title={ComponentFunctions.GetStatusDescription(ProviderId ? item.costStatus : item.status).description}
                                aria-label={ComponentFunctions.GetStatusDescription(ProviderId ? item.costStatus : item.status).description}
                              />
                        }
                        {StageId === undefined && (
                            <span style={{ fontSize: '0.8rem', color: '#495057' }}>{item.stageName || '—'}</span>
                        )}
                    </div>
                </TableCellLayout>
            ),
        }),
        ProviderId !== undefined && createTableColumn<ProtocolConsumption>({
            columnId: 'protocol',
            compare: (a, b) => a.protocolId - b.protocolId,
            renderHeaderCell: () => <span>{t('consumptionList.headers.protocol')}</span>,
            renderCell: (item) => (
                <TableCellLayout>
                    <Label>{GetProtocolById?.(item.protocolId)?.title}</Label>
                </TableCellLayout>
            ),
        }),
        StageId === undefined && createTableColumn<ProtocolConsumption>({
            columnId: 'patient',
            compare: (a, b) => (a.patientNumberInProtocol ?? '').localeCompare(b.patientNumberInProtocol ?? ''),
            renderHeaderCell: () => <span>{t('consumptionList.headers.patient')}</span>,
            renderCell: (item) => (
                <TableCellLayout><Label>{item.patientNumberInProtocol}</Label></TableCellLayout>
            ),
        }),
        createTableColumn<ProtocolConsumption>({
            columnId: 'concept',
            compare: (a, b) => a.serviceName.localeCompare(b.serviceName),
            renderHeaderCell: () => <span>{t('consumptionList.headers.concept')}</span>,
            renderCell: (item) => {
                // Mismo indicador de origen que la grilla general: sin él no se
                // distingue un consumo generado por el sistema de uno cargado a mano.
                const origin = GetConsumptionOrigin(item.origin);

                return (
                <TableCellLayout>
                    <div className='consumption-concept-cell'>
                        <span
                            className={'consumption-origin consumption-origin--' + origin.variant}
                            title={origin.label}
                            aria-label={origin.label}
                        >
                            {origin.variant === 'manual'
                                ? <Person16Regular />
                                : origin.variant === 'system'
                                    ? <Bot20Regular />
                                    : <QuestionCircle16Regular />}
                        </span>
                        <div className='consumption-concept-cell__content'>
                    <div>
                        {item.parentGroupingId && (
                            <span style={{ display: 'inline-block', marginRight: '10px', paddingTop: '5px' }}>
                                <BranchFork16Filled style={{ fontSize: FontSizes.size28, transform: 'rotate(90deg)', color: 'lightblue' }} />
                            </span>
                        )}
                        <Label><strong>{item.serviceName}</strong></Label>
                    </div>
                    <p className='item-badges'>
                        {item.condition === ProtocolConsumptionConditionEnum.ScreeningFailure && <Badge appearance="tint" size="medium"><span>{t('consumptionList.badges.selectionFailure')}</span></Badge>}
                        {item.typeOfService === 2 && <Badge appearance="filled" size="medium" color="danger"><Important16Regular />&nbsp;<span>{t('consumptionList.badges.additional')}</span></Badge>}
                        {item.typeOfService === 3 && <Badge appearance="outline" size="medium"><span>{t('consumptionList.badges.unexpected')}</span></Badge>}
                        {item.typeOfService === 4 && <Badge appearance="outline" size="medium"><span>{t('consumptionList.badges.notPerformed')}</span></Badge>}
                        {item.tag4?.length > 0 && <Badge appearance="filled" color="important"><AlertOn16Regular />&nbsp;<span>{t('consumptionList.badges.procedure')}</span></Badge>}
                        {item.tag3?.length > 0 && <Badge appearance="filled" color="warning"><CoinStack16Regular />&nbsp;<span>{t('consumptionList.badges.billable')}</span></Badge>}
                        {item.markedAsRefundable && <Badge><span>{t('consumptionList.badges.refundable')}</span></Badge>}
                    </p>
                        </div>
                    </div>
                </TableCellLayout>
                );
            },
        }),
        createTableColumn<ProtocolConsumption>({
            columnId: 'duedate',
            compare: (a, b) => new Date(a.serviceDueDate).getTime() - new Date(b.serviceDueDate).getTime(),
            renderHeaderCell: () => <span>{t('consumptionList.headers.dueDate')}</span>,
            renderCell: (item) => (
                <TableCellLayout>
                    {item.serviceDueDate && moment(item.serviceDueDate).format('DD/MM/YYYY hh:mm a')}
                </TableCellLayout>
            ),
        }),
        ProtocolId !== undefined && createTableColumn<ProtocolConsumption>({
            columnId: 'producer',
            renderHeaderCell: () => <span>{t('consumptionList.headers.producer')}</span>,
            renderCell: (item) => item.producer ? (
                <TableCellLayout media={<Avatar aria-label={item.producer} name={GetPersonByIdentifier(item.producer)?.fullName} color="colorful" />}>
                    {GetPersonByIdentifier(item.producer)?.fullName}
                </TableCellLayout>
            ) : <TableCellLayout>—</TableCellLayout>,
        }),
        createTableColumn<ProtocolConsumption>({
            columnId: 'totalAmount',
            compare: (a, b) => (a.totalAmount ?? 0) - (b.totalAmount ?? 0),
            renderHeaderCell: () => <span>{t('consumptionList.headers.totalAmount')}</span>,
            renderCell: (item) => (
                <TableCellLayout>
                    <strong><CurrencyAmountComponent amount={item.totalAmount} currency={item.currencyCode} /></strong>
                </TableCellLayout>
            ),
        }),
        createTableColumn<ProtocolConsumption>({
            columnId: 'withholding',
            compare: (a, b) => (a.withHolingAmount ?? 0) - (b.withHolingAmount ?? 0),
            renderHeaderCell: () => <span>{t('consumptionList.headers.withholding')}</span>,
            renderCell: (item) => (
                <TableCellLayout>
                    {!item.withHolingAmount
                        ? <span style={{ color: '#a19f9d' }}>—</span>
                        : <span title={`${item.withHolingPercentage ?? 0}%`}>
                            <CurrencyAmountComponent amount={item.withHolingAmount} currency={item.currencyCode} />
                          </span>
                    }
                </TableCellLayout>
            ),
        }),
        ...conceptColumnDefs,
        createTableColumn<ProtocolConsumption>({
            columnId: 'actions',
            renderHeaderCell: () => <span>{t('consumptionList.headers.actions')}</span>,
            renderCell: (item) => (
                <>
                    <Button appearance="subtle" aria-label="Ver detalle" icon={<EditRegular />} onClick={(e) => { e.stopPropagation(); onOpenDetails(item); }} />
                    {onDeleteConsumption && ![1, 2, 3, 6].includes(item.status) && (
                        <Button appearance="subtle" aria-label="Eliminar" icon={<DeleteRegular />} onClick={(e) => { e.stopPropagation(); onDeleteConsumption(item); }} />
                    )}
                </>
            ),
        }),
    ].filter(Boolean) as TableColumnDefinition<ProtocolConsumption>[];

    const columnSizingOptions = {
        status: { defaultWidth: 120, idealWidth: 120 },
        patient: { defaultWidth: 180, idealWidth: 180 },
        concept: { defaultWidth: 250, idealWidth: 250 },
        duedate: { defaultWidth: 90, idealWidth: 90 },
        producer: { defaultWidth: 150, idealWidth: 150 },
        totalAmount: { defaultWidth: 90, idealWidth: 90 },
        withholding: { defaultWidth: 90, idealWidth: 90 },
        ...Object.fromEntries(conceptColumnDefs.map(col => [col.columnId, { defaultWidth: 100, idealWidth: 100 }])),
        actions: { defaultWidth: 30, idealWidth: 30 },
    };

    if (visibleConsumptions.length === 0) {
        return <div style={{ padding: '12px 0', color: '#605e5c' }}>Sin consumos registrados.</div>;
    }

    return (
        <div style={{ overflowX: 'auto', minWidth: 0, maxWidth: 'calc(100vw - 17em)' }}>
            <DataGrid
                className='ConsumptionListGrid'
                items={visibleConsumptions}
                columns={columns.filter(Boolean) as TableColumnDefinition<ProtocolConsumption>[]}
                sortable
                selectionMode="multiselect"
                selectedItems={selectedRows}
                onSelectionChange={onSelectionChange}
                columnSizingOptions={columnSizingOptions}
                getRowId={(item) => item.id}
            >
                <DataGridHeader>
                    <DataGridRow
                        selectionCell={{
                            checkboxIndicator: { 'aria-label': 'Select all rows' },
                        }}
                    >
                        {({ renderHeaderCell }) => (
                            <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                        )}
                    </DataGridRow>
                </DataGridHeader>
                <DataGridBody<ProtocolConsumption>>
                    {({ item, rowId }) => (
                        <DataGridRow<ProtocolConsumption>
                            key={rowId}
                            className={GetRowClassByStatus(item.status)}
                            selectionCell={{
                                checkboxIndicator: { 'aria-label': 'Select row' },
                            }}
                        >
                            {({ renderCell }) => (
                                <DataGridCell
                                    onClick={(e: any) => {
                                        e.stopPropagation();
                                        onOpenDetails(item);
                                    }}
                                    style={{ cursor: 'pointer' }}
                                >{renderCell(item)}</DataGridCell>
                            )}
                        </DataGridRow>
                    )}
                </DataGridBody>
            </DataGrid>
        </div>
    );
};

// ─── Main component ──────────────────────────────────────────────────────────

const ProtocolConsumptionListByPatientComponent = (props: ComponentFunctions.Props) => {
    const [openItems, setOpenItems] = React.useState<string[]>([]);
    const [selectedConsumption, setSelectedConsumption] = React.useState<ProtocolConsumption | null>(null);
    const [isDetailOpen, setIsDetailOpen] = React.useState(false);
    const [selectedByPatient, setSelectedByPatient] = React.useState<Map<string, ProtocolConsumption[]>>(new Map());

    const onPatientSelectionChange = (patientKey: string, selected: ProtocolConsumption[]) => {
        setSelectedByPatient(prev => {
            const next = new Map(prev);
            next.set(patientKey, selected);
            const all = Array.from(next.values()).flat();
            if (props.onSelectionChange) props.onSelectionChange(all);
            return next;
        });
    };

    useEffect(() => {
        props.GetAllPractitioners(1, 1000, undefined);
    }, [props.ProtocolId]);

    // El protocolo tiene que estar en el store antes que los pacientes: el reducer
    // de MEDICALPROTOCOLS_GETALL_PROTOCOLPATIENTS_RECEIVE_ACTION escribe sobre el
    // protocolo de la lista y, si no está, descarta los pacientes en silencio.
    // Sin esto la pantalla se quedaba en "Cargando protocolo..." para siempre.
    useEffect(() => {
        if (props.ProtocolId && !props.protocol) {
            props.GetMedicalProtocolById(props.ProtocolId);
        }
    }, [props.ProtocolId, props.protocol]);

    // Los pacientes salen de protocol.patients, que nadie llenaba desde acá: la
    // vista se abría siempre en "no hay pacientes" salvo que otra pantalla los
    // hubiera cargado antes en el store. Se pide una sola vez por protocolo, ya
    // que un protocolo sin pacientes es una respuesta válida y volver a pedirlos
    // sería insistir contra el servidor sin motivo.
    const requestedPatientsForRef = React.useRef<number | undefined>(undefined);

    useEffect(() => {
        if (!props.ProtocolId || !props.protocol) {
            return;
        }

        if (requestedPatientsForRef.current === props.ProtocolId) {
            return;
        }

        requestedPatientsForRef.current = props.ProtocolId;
        props.GetProtocolPatients(props.ProtocolId, null);
    }, [props.ProtocolId, props.protocol]);

    const onToggleItem = (numberInProtocol: string) => {
        const isAlreadyOpen = openItems.includes(numberInProtocol);

        if (!isAlreadyOpen) {
            props.GetAllProtocolConsumptionsByPatient(props.ProtocolId, numberInProtocol);
            setOpenItems(prev => [...prev, numberInProtocol]);
        } else {
            setOpenItems(prev => prev.filter(id => id !== numberInProtocol));
        }
    };

    const onOpenDetails = (consumption: ProtocolConsumption) => {
        setSelectedConsumption(consumption);
        setIsDetailOpen(true);
    };

    if (!props.protocol) {
        return <Spinner label="Cargando protocolo..." />;
    }

    // El vacío se afirma sólo cuando la consulta ya volvió: mientras carga —o
    // antes de haberla lanzado— decir "no hay pacientes" es afirmar algo que
    // todavía no se sabe.
    const hasRequestedPatients = requestedPatientsForRef.current === props.ProtocolId;

    if (props.patients.length === 0 && (props.isLoadingPatients || !hasRequestedPatients)) {
        return <Spinner label="Cargando pacientes..." />;
    }

    if (props.patients.length === 0) {
        return (
            <div style={{ padding: 24, textAlign: 'center', color: '#605e5c' }}>
                <Person24Regular style={{ fontSize: 40, display: 'block', margin: '0 auto 8px' }} />
                <Text>No hay pacientes registrados en este protocolo.</Text>
            </div>
        );
    }

    return (
        <>
            <Accordion className="PatientAccordionList" multiple collapsible openItems={openItems as any}>
                {props.patients
                    .slice()
                    .sort((a, b) => (a.numberInProtocol ?? '').localeCompare(b.numberInProtocol ?? ''))
                    .map((patient: ProtocolPatient) => {
                        const key = patient.numberInProtocol;
                        const isLoading = props.patientConsumptionsLoading[key] === true;
                        const rawConsumptions = props.consumptionsByPatient[key];
                        const consumptions: ProtocolConsumption[] = Array.isArray(rawConsumptions) ? rawConsumptions : [];
                        const isOpen = openItems.includes(key);

                        return (
                            <AccordionItem
                                key={key}
                                value={key}
                                className={isOpen ? 'PatientAccordionItem--expanded' : ''}
                            >
                                <AccordionHeader
                                    onClick={() => onToggleItem(key)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <Avatar
                                            name={key}
                                            color="colorful"
                                            size={28}
                                        />
                                        <div>
                                            <Label style={{ fontWeight: 600 }}>
                                                Paciente #{key}
                                            </Label>
                                            {patient.medicalPractitioner?.fullName && (
                                                <Text size={200} style={{ marginLeft: 8, color: '#605e5c' }}>
                                                    — {patient.medicalPractitioner.fullName}
                                                </Text>
                                            )}
                                        </div>
                                        {isOpen && isLoading && (
                                            <Spinner size="extra-tiny" style={{ marginLeft: 'auto' }} />
                                        )}
                                        {isOpen && !isLoading && (
                                            <Badge appearance="tint" size="small" style={{ marginLeft: 'auto' }}>
                                                {consumptions.length} consumo{consumptions.length !== 1 ? 's' : ''}
                                            </Badge>
                                        )}
                                    </div>
                                </AccordionHeader>
                                <AccordionPanel>
                                    {isLoading
                                        ? <div style={{ padding: '16px 0', display: 'flex', justifyContent: 'center' }}><Spinner label="Cargando consumos..." /></div>
                                        : (
                                            <PatientConsumptionsGrid
                                                consumptions={consumptions}
                                                showGroupedByVisit={props.showGroupedByVisit}
                                                GetPersonByIdentifier={props.GetPersonByIdentifier}
                                                GetProtocolById={props.GetProtocolById}
                                                onOpenDetails={onOpenDetails}
                                                onDeleteConsumption={props.onDeleteConsumption}
                                                onRowSelectionChange={(selected) => onPatientSelectionChange(key, selected)}
                                                ProviderId={props.ProviderId}
                                                StageId={props.StageId}
                                                ProtocolId={props.ProtocolId}
                                            />
                                        )
                                    }
                                </AccordionPanel>
                            </AccordionItem>
                        );
                    })}
            </Accordion>

            {selectedConsumption && (
                <ConsumptionDetailsCompoment
                    ProtocolId={selectedConsumption.protocolId}
                    ParentProtocolConsumptionId={undefined}
                    ProtocolConsumptionId={selectedConsumption.id}
                    isOpen={isDetailOpen}
                    visitId={undefined}
                    onClose={() => { setSelectedConsumption(null); setIsDetailOpen(false); }}
                />
            )}
        </>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(ProtocolConsumptionListByPatientComponent as any);
